import React, {
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadOrInitUserSettings,
  shouldLoadUserSettings,
} from 'redux/reducers/userSettings';

import useMedia from 'hooks/useMedia';
import linearTransform from 'helpers/linearTransform';

import Artwork from 'components/base/Artwork/Artwork';
import Layout from 'components/styled/Layout/Layout';
import EntityMetadata from 'components/Header/EntityMetadata/EntityMetadata';
import SocialMetrics from './SocialMetrics';
import {
  ArtworkPositioner,
  EntityType,
  EntityTitle,
  Header,
  HeaderContainer,
  ContentWrapper,
  FollowEntityButtonWrapper,
  FollowersButton,
  ReleaseTypeWrapper,
} from './EntityHeader.style';
import { EntityContext } from 'context/entityContext';
import { RootState } from 'redux/rootReducer';
import SubscribeButton from 'components/notifications/Settings/SubscribeButton';
import { FollowEntityButton } from 'components/styled/FollowEntityButton/FollowEntityButton';
import { toggleFollowingEntity } from 'redux/reducers/user/following';
import { entityTypeToApiType } from 'helpers/mapEntityTypes';
import AddToListButton from 'components/customlists/AddToList/AddToListButton';
import Box from 'components/elements/Box';

interface IEntityHeaderProps {
  title?: string;
  /** The Entity ID */
  entityId?: string;
  /** The URL to the artwork of the specific entity */
  artworkSrc?: string;
  /** This indicates whether the Entity is in loading state */
  loading: boolean;
  /** The Entity data coming from the API */
  data: any;
  socialMetrics: any;
  artists: any;
}

/** EntityHeader displays the title of the current entity and its artwork when available. */
const EntityHeader: React.FC<IEntityHeaderProps> = (props) => {
  const context = useContext(EntityContext);
  const { entityType, isVersion, topic } = context;
  const {
    artists,
    artworkSrc,
    data,
    entityId,
    loading,
    title,
    socialMetrics,
  } = props;
  const [isFollowersShown, setIsFollowersShown] = useState(false);
  const isSmallScreen = useMedia('(max-width: 40em)');
  const canSubscribe = entityType === 'artist';
  const dispatch = useDispatch();

  const [followingEntities, followingIsLoading]: [any[], boolean] = useSelector(
    (state: RootState) => {
      return [
        state.userSettings?.data?.followingEntities,
        state.userSettings?.loading?.followingEntities,
      ];
    }
  );

  const titleComponent = title ? (
    <EntityTitle fontSize={linearTransform(title.length, isSmallScreen)}>
      {title}
    </EntityTitle>
  ) : (
    <EntityTitle />
  );

  const isFollowing = useMemo(() => {
    if (followingEntities) {
      return followingEntities.some(
        (item) =>
          item.id === entityId && item.type === entityTypeToApiType(entityType)
      );
    }

    return false;
  }, [entityId, entityType, followingEntities]);

  const toggleFollowing = useCallback(() => {
    if ('entity' in data) {
      return dispatch(toggleFollowingEntity(data.entity, isFollowing));
    }
  }, [dispatch, data, isFollowing]);

  useEffect(() => {
    if (dispatch(shouldLoadUserSettings())) {
      dispatch(loadOrInitUserSettings());
    }
  }, [dispatch]);

  return (
    <Layout.MaxWidth isNewDesign isWide relative>
      <HeaderContainer loading={loading}>
        <Header>
          <ArtworkPositioner>
            <Artwork
              key={`entity-artwork-${entityId}`}
              entityId={entityId}
              entityType={entityType}
              title={title}
              src={artworkSrc}
              isVersion={isVersion}
            />
          </ArtworkPositioner>
          <ContentWrapper data-pw="header-entity-metadata">
            <EntityType>
              {isVersion ? `${entityType} version` : entityType}
              {entityType === 'release' && (
                <ReleaseTypeWrapper>
                  {data?.entity?.productType}
                </ReleaseTypeWrapper>
              )}
            </EntityType>
            {titleComponent}
            <EntityMetadata
              key={`entity-metadata-${entityId}`}
              entityInfo={data}
              entityType={entityType}
              loading={loading}
              topic={topic}
              artists={artists}
            />
            {canSubscribe && isSmallScreen && (
              <>
                <FollowersButton
                  onClick={(): void => setIsFollowersShown(!isFollowersShown)}
                >
                  {isFollowersShown ? 'Hide' : 'Show'} Followers
                </FollowersButton>
                <SocialMetrics
                  socialMetrics={socialMetrics}
                  isActive={isFollowersShown}
                  isSmallScreen
                />
              </>
            )}
            <Box
              display="flex"
              justifyContent={['center', 'center', 'flex-start']}
            >
              {followingEntities && (
                <FollowEntityButtonWrapper>
                  <FollowEntityButton
                    isFollowing={isFollowing}
                    followingEntityCount={followingEntities.length}
                    hasTooltip
                    buttonType="toggle"
                    isLoading={followingIsLoading}
                    handleClick={toggleFollowing}
                  />
                </FollowEntityButtonWrapper>
              )}
              {entityId && title && canSubscribe && (
                <SubscribeButton entityName={title} entityId={entityId} />
              )}
              {entityId && title && (
                <AddToListButton
                  entityId={entityId}
                  entityName={title}
                  entityType={entityType}
                />
              )}
            </Box>
          </ContentWrapper>
          {canSubscribe && !isSmallScreen && (
            <SocialMetrics socialMetrics={socialMetrics} isActive />
          )}
        </Header>
      </HeaderContainer>
    </Layout.MaxWidth>
  );
};

export default EntityHeader;
